/**
 * 城市合伙人 API client —— 建档 / 列表 / 详情 / 停止合作 / 生成小程序码 /
 * 人工补录归因 / 佣金流水。
 *
 * 金额字段一律为「分」，展示层负责 /100。
 */
import { apiClient } from './client';

export type PartnerStatus = 'pending_bind' | 'active' | 'stopped';
export type CommissionStatus = 'pending' | 'payable' | 'settled' | 'void';
export type SettlementStatus = 'pending' | 'paid';

export interface PartnerDto {
  partnerId: string;
  name: string;
  wechatId: string | null;
  phone: string | null;
  city: string | null;
  status: PartnerStatus;
  /** 推广口令，同时是小程序码 scene 值 */
  promoCode: string;
  /** 一次性合作码，运营在微信里发给合伙人；无 partner:create 的角色后端返回 null */
  activationCode: string | null;
  boundUserId: string | null;
  boundAt: number | null;
  qrcodeUrl: string | null;
  note: string | null;
  stoppedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface PartnerSummaryDto extends PartnerDto {
  boundUserCount: number;
  convertedUserCount: number;
  pendingFen: number;
  payableFen: number;
  settledFen: number;
  payableCount: number;
}

/**
 * 归因明细。userId / displayName / phone 同时为 null = 扫了码但从未注册的访客，
 * 这种情况只有 identityHash 可作凭据，属正常状态而非数据缺失。
 */
export interface PartnerAttributionDto {
  attributionId: string;
  identityHash: string;
  partnerId: string;
  source: 'qrcode' | 'promo_code' | 'manual';
  boundAt: number;
  expireAt: number;
  convertedAt: number | null;
  convertedUserId: string | null;
  userId: string | null;
  displayName: string | null;
  phone: string | null;
}

export interface CommissionEntryDto {
  entryId: string;
  partnerId: string;
  userId: string;
  workId: string;
  userDisplayName: string | null;
  userPhone: string | null;
  workTitle: string | null;
  paymentRecordId: string;
  printOrderId: string | null;
  packageId: string;
  orderAmountFen: number;
  commissionFen: number;
  policyVersion: string;
  status: CommissionStatus;
  pendingAt: number;
  payableAt: number | null;
  settledAt: number | null;
  voidAt: number | null;
  voidReason: string | null;
  createdAt: number;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

export async function listPartners(filter: { status?: PartnerStatus; limit?: number } = {}) {
  const params: Record<string, string> = {};
  if (filter.status) params.status = filter.status;
  if (filter.limit != null) params.limit = String(filter.limit);
  const resp = await apiClient.get<ServiceResponse<{ items: PartnerSummaryDto[] }>>(
    '/partners',
    { params },
  );
  return resp.data.data.items;
}

export async function createPartner(input: {
  name: string;
  wechatId?: string;
  phone?: string;
  city?: string;
  note?: string;
}) {
  const resp = await apiClient.post<ServiceResponse<PartnerDto>>('/partners', input);
  return resp.data.data;
}

export async function getPartnerDetail(partnerId: string) {
  const resp = await apiClient.get<
    ServiceResponse<{ partner: PartnerSummaryDto; commissions: CommissionEntryDto[] }>
  >(`/partners/${encodeURIComponent(partnerId)}`);
  return resp.data.data;
}

export async function stopPartner(partnerId: string) {
  const resp = await apiClient.post<ServiceResponse<{ stopped: boolean }>>(
    `/partners/${encodeURIComponent(partnerId)}/stop`,
  );
  return resp.data.data;
}

export async function issuePartnerQrcode(partnerId: string) {
  const resp = await apiClient.post<ServiceResponse<{ qrcodeUrl: string }>>(
    `/partners/${encodeURIComponent(partnerId)}/qrcode`,
  );
  return resp.data.data;
}

/**
 * 人工补录归因。
 *
 * force=true 为**强制改归属**：无视现有归属直接改挂，是系统里唯一能纠正
 * 「归因错了」的通道（归因表 first-touch + 唯一键，写错的归属在正常路径下
 * 不可修正）。后端会把改动前后的归属一并落审计。
 */
export async function attributeManually(partnerId: string, userId: string, force = false) {
  const resp = await apiClient.post<
    ServiceResponse<{
      bound: boolean;
      alreadyBound: boolean;
      previousPartnerId: string;
      previousPartnerName: string;
    }>
  >(`/partners/${encodeURIComponent(partnerId)}/attribute-manual`, { userId, force });
  return resp.data.data;
}

/** 归因用户明细（含未成交）。 */
export async function listPartnerAttributions(partnerId: string, limit?: number) {
  const params: Record<string, string> = {};
  if (limit != null) params.limit = String(limit);
  const resp = await apiClient.get<ServiceResponse<{ items: PartnerAttributionDto[] }>>(
    `/partners/${encodeURIComponent(partnerId)}/attributions`,
    { params },
  );
  return resp.data.data.items;
}

export async function listCommissions(
  filter: { partnerId?: string; status?: CommissionStatus; limit?: number } = {},
) {
  const params: Record<string, string> = {};
  if (filter.partnerId) params.partnerId = filter.partnerId;
  if (filter.status) params.status = filter.status;
  if (filter.limit != null) params.limit = String(filter.limit);
  const resp = await apiClient.get<ServiceResponse<{ items: CommissionEntryDto[] }>>(
    '/commissions',
    { params },
  );
  return resp.data.data.items;
}


// ── 结算单（P2）────────────────────────────────────────────────────────────

export interface SettlementBatchDto {
  batchId: string;
  partnerId: string;
  periodLabel: string;
  entryCount: number;
  totalFen: number;
  status: SettlementStatus;
  cutoffAt: number;
  generatedAt: number;
  generatedBy: string;
  paidAt: number | null;
  paidBy: string | null;
  transferNote: string | null;
}

export interface SettlementPreviewItemDto {
  partnerId: string;
  partnerName: string;
  entryCount: number;
  totalFen: number;
}

export async function listSettlements(
  filter: { partnerId?: string; status?: SettlementStatus; limit?: number } = {},
) {
  const params: Record<string, string> = {};
  if (filter.partnerId) params.partnerId = filter.partnerId;
  if (filter.status) params.status = filter.status;
  if (filter.limit != null) params.limit = String(filter.limit);
  const resp = await apiClient.get<ServiceResponse<{ items: SettlementBatchDto[] }>>(
    '/settlements',
    { params },
  );
  return resp.data.data.items;
}

export async function previewSettlement(cutoffAt?: number) {
  const params: Record<string, string> = {};
  if (cutoffAt != null) params.cutoffAt = String(cutoffAt);
  const resp = await apiClient.get<
    ServiceResponse<{ cutoffAt: number; items: SettlementPreviewItemDto[] }>
  >('/settlements/preview', { params });
  return resp.data.data;
}

export async function generateSettlement(cutoffAt?: number) {
  const resp = await apiClient.post<
    ServiceResponse<{
      periodLabel: string;
      cutoffAt: number;
      batches: Array<{ batchId: string; partnerId: string; entryCount: number; totalFen: number }>;
    }>
  >('/settlements/generate', cutoffAt != null ? { cutoffAt } : {});
  return resp.data.data;
}

export async function markSettlementPaid(batchId: string, transferNote?: string) {
  const resp = await apiClient.post<
    ServiceResponse<{ batch: SettlementBatchDto; settledEntryCount: number }>
  >(`/settlements/${encodeURIComponent(batchId)}/mark-paid`, transferNote ? { transferNote } : {});
  return resp.data.data;
}
